import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';

import useUniqueTableName from '../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import useNewColumnsVisibleByDefault from '../../hooks/useNewColumnsVisibleByDefault';
import useModalProps from '../../hooks/useModalProps';
import useHasPermission from '../../hooks/useHasPermission';
import useIsAffordableProperty from '../../hooks/useIsAffordableProperty';
import usePropertyHasProgram from '../../hooks/usePropertyHasProgram';
import { AFFORDABLE_PROGRAMS } from '../../utils/affordable';
import { BuildingService } from '../../services/buildingService';
import {
  useAsyncStates,
  useAsyncPropertyConfigs,
} from '../ManageBuildings/hooks';
import v1Messages from '../ManageBuildings/messages';
import appMessages from '../App/messages';

import useHeaders from './hooks.useHeaders';
import useRows from './hooks.useRows';
import useExport from './hooks.useExport';
import useShowLateColumns from './hooks.useShowLateColumns';
import useUpdateBuilding, {
  buildingsQueryKey,
} from './hooks.useUpdateBuilding';
import { getConstructionTypeOptions, parseBuildings } from './utils';
import messages from './messages';
import {
  EDIT_PERMISSION,
  GATED_COLUMN_IDS,
  NAME,
  PROPERTY_PATH_MAP,
} from './constants';
import type { Gates } from './types';

// Same column gates as ManageBuildings/BuildingsTable/index.js
export const useBuildingGates = (selectedProperty: Object): Gates => {
  const { publicHousing, naur, bldgCertoccConstypeFields } = useFlags();
  const isAffordableProperty = useIsAffordableProperty(selectedProperty);
  const isRDProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.RD,
  );
  const isLIHTCProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.LIHTC,
  );
  const isBONDProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.BOND,
  );
  const hasPHProgram = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.PH,
  );
  const stateId = selectedProperty?.state?.id;

  return useMemo(() => {
    const isPHProperty = hasPHProgram && Boolean(publicHousing);
    return {
      isAffordableProperty,
      isRDProperty,
      isLIHTCProperty,
      isPHProperty,
      naur: Boolean(naur),
      showFundingType:
        isAffordableProperty &&
        stateId === 'US-CA' &&
        (isLIHTCProperty || isBONDProperty),
      showCertOccConstructionType:
        Boolean(bldgCertoccConstypeFields) &&
        isAffordableProperty &&
        isLIHTCProperty,
      showFractions: isAffordableProperty && isLIHTCProperty && Boolean(naur),
    };
  }, [
    hasPHProgram,
    publicHousing,
    isAffordableProperty,
    isRDProperty,
    isLIHTCProperty,
    isBONDProperty,
    naur,
    stateId,
    bldgCertoccConstypeFields,
  ]);
};

const useManageBuildingsV2 = ({
  selectedProperty,
  intl,
  promptToaster,
}: {
  selectedProperty: Object,
  intl: Object,
  promptToaster: Function,
}) => {
  const { id: propertyId, organizationId } = selectedProperty ?? {};
  const name = useUniqueTableName(NAME);
  const gates = useBuildingGates(selectedProperty);
  const canEdit = useHasPermission(EDIT_PERMISSION);

  const {
    data: buildings = [],
    isLoading,
    isError,
  } = useQuery(
    buildingsQueryKey(organizationId, propertyId),
    () => new BuildingService().getAll(organizationId, propertyId),
    {
      enabled: Boolean(organizationId && propertyId),
      onError: () => {
        promptToaster({
          type: 'error',
          title: intl.formatMessage(v1Messages.error),
          message: intl.formatMessage(messages.loadError),
        });
      },
    },
  );

  const { states } = useAsyncStates();
  const { singleBuildingElection, updateSingleBuildingElection } =
    useAsyncPropertyConfigs(selectedProperty, promptToaster, intl);

  const constructionTypeOptions = useMemo(
    () => getConstructionTypeOptions(intl, v1Messages),
    [intl],
  );

  const results = useMemo(
    () =>
      parseBuildings({
        buildings,
        yesLabel: intl.formatMessage(appMessages.yes),
        noLabel: intl.formatMessage(appMessages.no),
        constructionTypeOptions,
      }),
    [buildings, intl, constructionTypeOptions],
  );

  const headers = useHeaders({ gates, intl, constructionTypeOptions });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({ name, headers });

  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const editModalProps = useModalProps();

  const rows = useRows({
    buildings: sortedAndFilteredResults,
    canEdit,
    intl,
    openEdit: editModalProps.handleOpen,
  });

  const {
    initialColumnsOrder: actionsFirstColumnsOrder,
    shouldForceOverwrite: actionsFirstForceOverwrite,
  } = useActionsFirstColumnOrder({ name, headers });

  // The gated columns can arrive a render after the first one (flags and the selected property
  // load late), after the table has already saved its visible columns. Show them once instead of
  // leaving them hidden, without undoing columns the user hides later.
  const { initialColumnsOrder, initialSelectedColumns, shouldForceOverwrite } =
    useNewColumnsVisibleByDefault({
      name,
      headers,
      introducedColumnIds: GATED_COLUMN_IDS,
      initialColumnsOrder: actionsFirstColumnsOrder,
      forceOverwrite: actionsFirstForceOverwrite,
    });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    initialSelectedColumns,
    forceOverwrite: shouldForceOverwrite,
  });

  useShowLateColumns({
    headers,
    initialSelectedColumns,
    selectedColumns,
    handleColumnChange,
  });

  const hasAnyFilters = Boolean(
    Object.keys(filterState ?? {}).length ||
      Object.keys(searchState ?? {}).length ||
      Object.keys(dateState ?? {}).length,
  );

  const { onCSVButtonClick, onPDFButtonClick } = useExport({
    filteredHeaders,
    rows,
    hasAnyFilters,
    propertyName: selectedProperty?.name,
    intl,
  });

  const updateBuilding = useUpdateBuilding({
    organizationId,
    propertyId,
    promptToaster,
    intl,
  });

  return {
    name,
    gates,
    canEdit,
    buildings,
    isLoading,
    // A failed refetch keeps the last good list; only a load with nothing to show is an error.
    isError: isError && buildings.length === 0,
    count: rows.length,
    totalCount: results.length,
    rows,
    headers,
    filteredHeaders,
    order,
    orderBy,
    handleSortChange,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    handleColumnChange,
    onCSVButtonClick,
    onPDFButtonClick,
    states,
    singleBuildingElection,
    updateSingleBuildingElection,
    editModalProps,
    updateBuilding,
    constructionTypeOptions,
  };
};

export default useManageBuildingsV2;
